import { Category } from '@@types'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import { ReactSVG } from 'react-svg'

import { URL_PAGE_CATALOG, URL_PAGE_CATALOG_1ST_LVL } from '@shared/contants'
import { cn } from '@shared/lib/cn'
import { LocalizedLink as Link } from '@shared/lib/locale'
import CardLink from '@shared/ui/card-link'

import styles from './styles.module.scss'

interface CategoriesPreviewProps {
  categories: Category[];
  loading?: boolean;
  classNameGrid?: string;
  classNameItem?: string;
  classNamePlug?: string;
  catalogLink?: ReactNode;
  withoutDifferentSize?: boolean;
}

interface CategoryCardProps {
  category: Category;
  classNameItem?: string;
  classNamePlug?: string;
  withoutDifferentSize?: boolean;
}

function CategoryCard ({ category, classNameItem, classNamePlug, withoutDifferentSize }: CategoryCardProps) {
  return (
    <Link
      className={cn(
        styles.root__item,
        category?.preview_size && !withoutDifferentSize
          ? styles[category.preview_size]
          : '',
        classNameItem,
      )}
      to={generatePath(URL_PAGE_CATALOG_1ST_LVL, { categorySlug: category?.slug as string })}
      style={category?.icon?.url || category.altIcon?.url ? { backgroundImage: `url(${category.icon?.url ?? category.altIcon?.url})` } : undefined}
    >
      {category?.name}

      {!category?.icon?.url && (
        <ReactSVG
          className={cn(styles.root__plug, classNamePlug)}
          src="/icons/flower.svg"
          wrapper="span"
        />
      )}
    </Link>
  )
}

function CategoriesSkeleton ({ classNameGrid }: { classNameGrid?: string }) {
  return (
    <div className={cn(styles.root, classNameGrid)}>
      {Array.from({ length: 8 }).map((_, index) => (
        <div key={index} className={cn(styles.root__item, styles.skeleton)}>
          <div className={styles.skeleton__text}></div>
          <div className={styles.skeleton__icon}></div>
        </div>
      ))}
    </div>
  )
}

export default function CategoriesPreview (props: CategoriesPreviewProps) {
  const { t: tc } = useTranslation('common')
  const {
    categories,
    loading = false,
    catalogLink,
    classNameItem,
    classNameGrid,
    classNamePlug,
    withoutDifferentSize = false,
  } = props

  if (loading) {
    return <CategoriesSkeleton classNameGrid={classNameGrid} />
  }

  if (!categories?.length) {
    return null
  }

  return (
    <div className={cn(styles.root, classNameGrid)}>
      {categories?.map((category) => (
        <CategoryCard
          key={category?.id}
          category={category}
          classNameItem={classNameItem}
          classNamePlug={classNamePlug}
          withoutDifferentSize={withoutDifferentSize}
        />
      ))}

      {catalogLink && (
        <CardLink className={styles.root__card} to={URL_PAGE_CATALOG}>
          <h3 className={styles['root__all-title']}>{tc('full_catalog')}</h3>
        </CardLink>
      )}
    </div>
  )
}
